import { useCallback, useEffect, useMemo, useRef, useState } from "react";

import {
	ICatalogue,
	ICatalogueCustomization,
	ICatalogueSetItem,
} from "../../../../apis/useGetCatalogueData/types";
import { globalEvents } from "../../../../common/helpers/GlobalEvents";
import { Nullable } from "../../../../common/types/types";
import { PDP_EVENT } from "../../constants";
import { IPricedAccordionSelection } from "./types";

const NO_CUSTOMIZATIONS: ICatalogueCustomization[] = [];

export const useSetItemChangeHandler = (
	idx: number,
	setitem: ICatalogueSetItem,
	items: ICatalogue[],
	selectedSetItem?: ICatalogueSetItem | null
) => {
	const [selecteditem, setSelecteditem] = useState<Nullable<ICatalogue>>(null);
	const initializationRef = useRef<string | null>(null);
	const mountedRef = useRef<boolean>(true);

	// Reset on mount
	useEffect(() => {
		mountedRef.current = true;
		initializationRef.current = null;

		return () => {
			mountedRef.current = false;
		};
	}, [idx, setitem.external_id]);

	// Sync selecteditem with selectedSetItem prop
	useEffect(() => {
		if (!mountedRef.current) return;

		if (selectedSetItem) {
			const selectedId = selectedSetItem.food_supplier_menu_item_id;

			if (selectedId != null && initializationRef.current !== String(selectedId)) {
				// Find the corresponding ICatalogue item from items array
				const catalogueItem = items.find((item) => item.id === String(selectedId));

				if (catalogueItem) {
					setSelecteditem(catalogueItem);
					initializationRef.current = String(selectedId);
				}
			}
		} else if (initializationRef.current !== null) {
			setSelecteditem(null);
			initializationRef.current = null;
		}
	}, [selectedSetItem?.food_supplier_menu_item_id, items]);

	const onChange = useCallback(
		(selections: IPricedAccordionSelection[]) => {
			const fn = (selected: string[]) => {
				const selected_item = selections.find(({ id }) => id === selected[0]);
				const price_diff = selected_item?.price_diff ?? 0;

				globalEvents.emit(PDP_EVENT.UPDATE_SETITEM, { idx, setitem, items, selected, price_diff });
			};

			return fn;
		},
		[idx, setitem.external_id]
	);

	const customizations = useMemo(() => {
		if (!selecteditem) return NO_CUSTOMIZATIONS;

		return selecteditem.food_supplier_customizations ?? NO_CUSTOMIZATIONS;
	}, [selecteditem?.food_supplier_customizations]);

	return {
		onChange,
		customizations,
		selecteditem,
	};
};
